// First-party analytics tracker.
// Usage:
//   import { track, useTracking } from "@/lib/analytics";
//   useTracking();                                    // in root layout: fires page_view on route change
//   track("hotel_click", { hotel_id, hotel_name });   // manual events
//
// Visitor ID: persisted in localStorage for 1 year (fresh UUID otherwise).
// Session ID: sessionStorage — resets per tab.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Route patterns we do NOT want to count (admin/hotel/checkout previews would skew data).
const IGNORED_PREFIXES = ["/admin", "/hotel-panel", "/hotel-confirm"];

function getVisitorId() {
    try {
        let vid = localStorage.getItem("mst_vid");
        if (!vid) {
            vid = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
            localStorage.setItem("mst_vid", vid);
        }
        return vid;
    } catch {
        return "";
    }
}

function getSessionId() {
    try {
        let sid = sessionStorage.getItem("mst_sid");
        if (!sid) {
            sid = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
            sessionStorage.setItem("mst_sid", sid);
        }
        return sid;
    } catch {
        return "";
    }
}

export function track(event_type, extra = {}) {
    try {
        const path = window.location.pathname + window.location.search;
        if (IGNORED_PREFIXES.some((p) => path.startsWith(p))) return;

        const payload = {
            event_type,
            path,
            referrer: document.referrer || "",
            visitor_id: getVisitorId(),
            session_id: getSessionId(),
            screen_width: window.innerWidth || null,
            ...extra,
        };

        // Non-blocking: prefer sendBeacon if available (works even during page unload)
        const url = `${API}/track`;
        const body = JSON.stringify(payload);
        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            navigator.sendBeacon(url, blob);
            return;
        }
        // Fallback: fire-and-forget
        axios.post(url, payload, { timeout: 3000 }).catch(() => {});
    } catch {
        /* swallow — analytics must never crash the app */
    }
}

/** Hook that fires page_view on every route change. Put it once in a top-level layout. */
export function useTracking() {
    const location = useLocation();
    useEffect(() => {
        // Small delay so document.title/route data is settled.
        const t = setTimeout(() => track("page_view"), 150);
        return () => clearTimeout(t);
    }, [location.pathname, location.search]);
}
